import { FadeUp, SectionTag } from "../components/motion";
import { WHY_SGS, IMAGES } from "../data/content";
import useSEO from "../hooks/useSEO";
import { Target, Flag, Users, Handshake } from "lucide-react";

const JOURNEY = [
  { year: "2014", text: "SATI GLOBAL SYSTEMS established in Patna — software development and IT services for regional clients." },
  { year: "Growth", text: "Capability expanded into chip-level hardware maintenance, networking and IT infrastructure delivery." },
  { year: "Scale", text: "CCTV surveillance, video conferencing and business process automation added; on-site and offshore delivery model formalized." },
  { year: "Today", text: "A multidisciplinary technology partner serving enterprise, institutional, government and public-sector clients." },
];

export default function About() {
  useSEO(
    "About SATI Global Systems (SGSNet) | IT, CCTV & Surveillance Company in Patna since 2014",
    "Learn about SATI Global Systems (SGSNet) — a Patna-based MSME registered, GeM authorized technology company since 2014, delivering CCTV surveillance, networking, software, automation and IT consultancy with a documented Quality Management System."
  );
  return (
    <main data-testid="about-page" className="pt-16 sm:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>About SGS</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl">
            Engineering trust, <span className="text-blue-600">since 2014.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            SATI GLOBAL SYSTEMS is a technology solutions company delivering software development,
            website development, SEO, chip-level system maintenance, business process automation,
            CCTV surveillance, video conferencing, networking, IT solutions, IT & HR consultancy
            and project-management support.
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="about-credentials">
            <span className="px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-mono uppercase tracking-[0.15em]">MSME Registered</span>
            <span className="px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-mono uppercase tracking-[0.15em]">GeM Authorized Supplier</span>
            <span className="px-4 py-2 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-mono uppercase tracking-[0.15em]">Est. 2014 · Patna HQ</span>
          </div>
        </FadeUp>

        <div className="mt-20 grid lg:grid-cols-2 gap-14">
          <div>
            <FadeUp><h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Our Journey</h2></FadeUp>
            <div className="mt-8 space-y-0 relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-800" />
              {JOURNEY.map((j, i) => (
                <FadeUp key={i} delay={i * 0.1}>
                  <div className="relative pl-8 pb-8" data-testid={`journey-item-${i}`}>
                    <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-blue-600 bg-obsidian" />
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">{j.year}</span>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{j.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <FadeUp>
              <div className="bg-panel border border-slate-200 rounded-xl p-7" data-testid="vision-card">
                <Target size={22} className="text-blue-600" />
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">Vision</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  To become a trusted global technology partner delivering innovative, reliable
                  and scalable digital solutions that create measurable business value.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="bg-panel border border-slate-200 rounded-xl p-7" data-testid="mission-card">
                <Flag size={22} className="text-blue-600" />
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">Mission</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  To combine technology expertise, engineering excellence and disciplined project
                  execution to deliver secure, efficient and sustainable solutions for our clients.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="bg-panel border border-slate-200 rounded-xl p-7" data-testid="partnerships-card">
                <Handshake size={22} className="text-blue-600" />
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">Strategic Relationships</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  SGS works through strategic relationships with industry experts and technology
                  partners, extending in-house capability with specialized domain depth whenever
                  an engagement demands it.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>

        <div className="mt-24 relative rounded-2xl overflow-hidden border border-slate-200">
          <img src={IMAGES.boardroom} alt="SGS team at work" className="w-full h-[320px] object-cover spotlight-img" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFFFF]/90 via-[#FFFFFF]/40 to-transparent flex items-center">
            <div className="px-8 sm:px-12 max-w-lg">
              <Users size={22} className="text-blue-600" />
              <h2 className="mt-4 font-heading text-2xl font-bold text-slate-900">Leadership & Team</h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                A management team with hands-on engineering roots leads multidisciplinary squads
                across software, hardware, networking and surveillance — with single-point
                ownership on every engagement.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-24">
          <FadeUp><SectionTag>Why Clients Stay</SectionTag></FadeUp>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_SGS.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.07}>
                <div className="bg-panel border border-slate-200 rounded-xl p-6 hover:border-blue-600/40 transition-colors h-full">
                  <w.icon size={20} className="text-blue-600" strokeWidth={1.7} />
                  <h3 className="mt-3 font-heading font-semibold text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{w.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
